import circleBg from '@/assets/notifications/withdrawal-approved/medallion-circle-bg.svg';
import checkIllustration from '@/assets/notifications/withdrawal-approved/medallion-check.svg';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { dollarsToCents, formatCurrency } from '@/lib/format-currency';
import { metaNumber, metaString } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['withdrawal-approved'];

/** A single decorative 4-point sparkle (green celebration layer, positional). */
function Sparkle({ className, size = 12 }: { className?: string; size?: number }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
      <path d="M12 0c.8 5.9 5.3 10.4 12 12-6.7 1.6-11.2 6.1-12 12-.8-5.9-5.3-10.4-12-12C6.7 10.4 11.2 5.9 12 0Z" />
    </svg>
  );
}

/**
 * WITHDRAW_APPROVED — positive (green) confirmation. Green check medallion with a
 * scattered green sparkle layer, then the celebratory amount block: a small lead
 * line, the withdrawal amount as the largest type on any pop-up (50px SemiBold),
 * and a green "approved!" tail. CTA "Awesome" just dismisses.
 *
 * Amount comes from `meta.amount` (dollars) + `meta.currency` (the WITHDRAW_APPROVED
 * producer emits both); rendered via the shared currency formatter so it matches
 * the rest of the app ($50.00).
 */
export function WithdrawApprovedPopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const amount = metaNumber(meta, 'amount');
  const currency = metaString(meta, 'currency') ?? 'usd';
  const amountLabel = amount !== undefined ? formatCurrency(dollarsToCents(amount), currency) : undefined;

  return (
    <>
      <div className="relative flex items-center justify-center text-success">
        <Sparkle className="absolute -left-1 top-2" size={14} />
        <Sparkle className="absolute left-6 -top-2" size={10} />
        <Sparkle className="absolute right-3 -top-1" size={12} />
        <Sparkle className="absolute -right-2 top-1/2" size={16} />
        <Sparkle className="absolute left-0 bottom-4" size={10} />
        <Medallion circleSrc={circleBg} illustrationSrc={checkIllustration} tone="positive" size={140} alt="" />
      </div>

      <div className="flex flex-col items-center gap-3.5">
        <p className="text-xl text-ink">Your withdrawal request of</p>

        {amountLabel && (
          <p className="text-[50px] font-semibold leading-none tracking-[-1px] text-ink" data-testid="withdrawal-amount">
            {amountLabel}
          </p>
        )}

        <p className="text-xl text-ink">
          has been <span className="font-semibold text-success">approved!</span>
        </p>
      </div>

      <PopupCta
        label={CTA.primary.label}
        width={270}
        onClick={() => onAction(CTA.primary.action)}
        testId="popup-cta-primary"
      />
    </>
  );
}
